<template>
  <div v-if="isLoading" class="spinner-container">
    <Spinner />
  </div>
  <iframe
    v-show="!isLoading"
    id="onboarding"
    title="onboarding"
    :src="incodeUrl"
    allowusermedia
    allow="geolocation; microphone; camera;"
    class="iframe-incode"
    @load="handleIframeLoaded"
  >
  </iframe>
  <div v-if="iOSDevice" @click="handleCloseIframe" class="close-iframe-link">
    <a href="#">{{ t("INCODE_CONTINUE_WITH_SALE") }}</a>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watchEffect } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useI18n } from "@/apps/merchant3/composables/useI18n";
import { incodeOnBoard } from "@/apps/merchant3/services/incode.service";
import logAction from "@/apps/merchant3/services/action-logs.service";
import logEvent from "@/apps/merchant3/services/event-logs.service";
import ActionLogs from "@/apps/merchant3/types/ActionLogEvents";
import useCartPersistState from "@/apps/merchant3/hooks/useCartPersistState";
import useIncodeOnBoardStatus from "@/apps/merchant3/hooks/useIncodeOnBoardStatus";
import useMerchantPersistState from "@/apps/merchant3/hooks/useMerchantPersistState";
import { RouteName } from "@/apps/merchant3/router/RouteName";
import { getCountryCode } from "@/apps/merchant3/helpers/flowType";
import EventLogs from "@/apps/merchant3/types/EventLogs";
import MerchantFlags from "@/apps/merchant3/types/MerchantFlags";
import { MerchantCountry } from "@/types/merchant";
import { isIOSDevice } from "@/apps/merchant3/shared-helpers/device-detect.helper";
import { isEcommerceFlow } from "@/apps/merchant3/helpers/checkoutFlows";
import Spinner from "@/apps/merchant3/shared-components/ui/Spinner.vue";

export type QrCodeIframeLoadedEventPayload = { loadTime: number };
export type IncodeUrlLoadedEventPayload = {
  incodeUrl: string;
  loadTime: number;
};

const emit = defineEmits(["incodeUrlLoaded", "qrCodeIframeLoaded"]);

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const incodeUrl = ref("");
const errorDisplay = ref("");
const iOSDevice = ref(false);
const isLoading = ref(true);

const canUseCreditCheckPermissionsPage = computed(
  () =>
    store.state.merchant?.merchantFlags?.includes(
      MerchantFlags.USE_CREDIT_CHECK_PERMISSION_PAGE
    ) && !isEcommerceFlow.value
);

const merchantCountry = computed(
  () => store?.state.merchant?.merchant?.country || ""
);

watchEffect(() => {
  if (store?.state.carts?.incodeOnboardComplete) {
    if (isIOSDevice()) {
      iOSDevice.value = true;
    }
    nextStep();
  }
});

const nextStep = () => {
  if (merchantCountry.value === MerchantCountry.MX) {
    router.push({
      name: RouteName.M3CXWaitValidationView,
      params: { countryCode: getCountryCode(), userTag: store.state.carts?.userTag },
    });
  } else if (canUseCreditCheckPermissionsPage.value) {
    router.push({ name: RouteName.M3CreditCheckPermissionView, params: { countryCode: getCountryCode() } });
  } else {
    router.push({ name: RouteName.M3CustomerContactInfoView, params: { countryCode: getCountryCode() } });
  }
};

const loadIncodeUrl = () => {
  logEvent(EventLogs.Events.INCODE_START);
  const incodeUrlRequestStart = performance.now();

  incodeOnBoard(store.state.carts.cartTag)
    .then((response) => {
      incodeUrl.value = response.data.url;
      //EventLog
      logEvent(EventLogs.Events.INCODE_ONBOARD, { success: true });
      //ActionLog
      logAction(ActionLogs.Events.INCODE_ONBOARD, { success: true });

      useIncodeOnBoardStatus();

      const incodeUrlRequestLoadTime =
        performance.now() - incodeUrlRequestStart;

      emit("incodeUrlLoaded", {
        incodeUrl: incodeUrl.value,
        loadTime: incodeUrlRequestLoadTime,
      });
    })
    .catch((error) => {
      errorDisplay.value = error.message;
      //EventLog
      logEvent(EventLogs.Events.INCODE_ONBOARD, {
        success: false,
        error: error.message,
      });
      //ActionLog
      logAction(ActionLogs.Events.INCODE_ONBOARD, {
        success: false,
        error: error.message,
      });
    });
  return true;
};

const iFrameLoadStartTime = ref(0);
const handleIframeLoaded = () => {
  const loadTime = performance.now() - iFrameLoadStartTime.value;

  // Emit the event only when the incode url is present to measure the
  // real load time of the iframe with loaded content.
  if ((incodeUrl.value ?? "").length > 0) {
    emit("qrCodeIframeLoaded", { loadTime: loadTime });
    isLoading.value = false;
  }
};

onMounted(() => {
  iFrameLoadStartTime.value = performance.now();
  useMerchantPersistState(() => true);
  useCartPersistState(loadIncodeUrl);
});

const handleCloseIframe = () => {
  const incodeFrame = window.parent.document.getElementById(
    "onboarding"
  ) as HTMLIFrameElement;
  incodeFrame.remove();
  nextStep();
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
.iframe-incode {
  flex-grow: 1;
}
.close-iframe-link {
  padding-top: 10px;
  padding-left: 16px;
  color: $pj-color-green;
  font-weight: bold;
  a {
    text-decoration: none;
    color: $pj-color-green;
  }
}
.spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 400px;
}
</style>
